declare var FullCalendar: any;

module Bookhus.Widgets {
    interface IDayCell extends HTMLElement {
        fcMultiMonthHightlight?: HTMLElement
    }

    interface IDateRange {
        startDate: Date | string | null,
        endDate: Date | string | null
    }

    interface IBookFullCalendarOptions {
        schedulerLicenseKey: string;
        culture: string;
        houseId: number;
        userId: number;

        initialDate: Date | string;
        initialSelection: IDateRange | null,

        mediumBookingBorder: number;
        minBookingBorder: number;

        reservationRestrictions: boolean;
        isDayOpen: boolean[];
        separateTime: number;
        showDaysBeforeTodayAsBooked: boolean;
        showDaysAfterTodayAsBooked: number;
        isAdmin: boolean;
        isExternalUser: boolean;
        showAmountOfMonth: number;
        calendarStatusDisplayMode: CalendarStatusDisplayMode;
    }

    class EventEmitter {
        private handlers: { [eventName: string]: Function[] } = {};

        on(event: string, handler: Function) {
            if (!this.handlers[event]) {
                this.handlers[event] = [];
            }
            this.handlers[event].push(handler);
        }

        off(event: string, handler?: Function) {
            if (!this.handlers[event]) {
                return;
            }

            if (handler) {
                this.handlers[event] = this.handlers[event].filter(func => func !== handler);
            }
            else {
                delete this.handlers[event];
            }

        }

        trigger(event: string, ...args: any[]) {
            if (!this.handlers[event]) {
                return;
            }

            for (var handler of this.handlers[event]) {
                handler(...args);
            }
        }
    }

    class DateSelection {
        private calendar: any;
        private options: IBookFullCalendarOptions;

        private cellMap: Map<string, IDayCell>;
        private highlightedCells: IDayCell[];

        private startDate: Date;
        private endDate: Date;

        private clickHandlerBind: ((event: MouseEvent) => void) | null = null;

        constructor(calendar: any, options: IBookFullCalendarOptions) {
            this.calendar = calendar;
            this.options = options;

            this.calendar.on('datesSet', this.handleDatesChanged.bind(this));
            this.calendar.on('dateClick', this.handleClick.bind(this));
            this.calendar.on('select', this.handleSelect.bind(this));
        }

        private handleSelect(selectionInfo): void {
            this.startDate = selectionInfo.start;
            // Remove one day from selection, since fc behaviour is to exclude end date
            this.endDate = selectionInfo.end.addDays(-1);
        }

        private handleDatesChanged(eventInfo): void {
            this.highlightedCells = [];
            this.cellMap = new Map<string, IDayCell>();
            this.calendar.el.querySelectorAll('td[data-date]').forEach((td: IDayCell) => {
                this.cellMap.set(td.dataset.date, td);
            });

            if (this.startDate && this.endDate) {
                this.highlight(this.startDate, this.endDate);
            }
        }

        private handleClick(info): void {
            var { dayEl }: { dayEl: IDayCell } = info;

            if (!dayEl?.dataset?.date || dayEl.querySelector('.day-not-active') != null) {
                return;
            }

            if (this.startDate && !this.endDate) {
                this.endDate = new Date(dayEl.dataset.date);

                if (this.startDate > this.endDate) {
                    var tempDate = this.startDate;
                    this.startDate = this.endDate;
                    this.endDate = tempDate;
                }

                this.endDate = this.endDate.addDays(1);
                this.clearHighlight();

                this.calendar.select(this.startDate, this.endDate);

                this.calendar.el.removeEventListener('mousemove', this.clickHandlerBind);
                this.clickHandlerBind = null;
                return;
            }

            this.clickHandlerBind = this.handleMouseMove.bind(this);
            this.calendar.el.addEventListener('mousemove', this.clickHandlerBind);

            this.startDate = new Date(dayEl.dataset.date);
            this.endDate = null;

            if (this.options.showAmountOfMonth == 1) {
                // Search immediately, but keep the first click as the pending range start.
                this.calendar.select(new Date(this.startDate), this.startDate.addDays(1));
                // The select handlers update these dates, so restore the pending selection.
                this.startDate = new Date(dayEl.dataset.date);
                this.endDate = null;
            }

            this.highlight(this.startDate, this.startDate);
        }

        private handleMouseMove(event: MouseEvent): void {
            var hoveredCell = this.findTdFromEvent(event);
            if (hoveredCell) {
                var hoveredDate = new Date(hoveredCell.dataset.date);
                this.highlight(this.startDate, hoveredDate);
            }
        }

        private clearHighlight(): void {
            this.highlightedCells.forEach(td => {
                td.fcMultiMonthHightlight?.remove();
                delete td.fcMultiMonthHightlight;
            });
            this.highlightedCells = [];
        }

        private highlight(startDate: Date, endDate: Date): void {
            // Check if selection is in the current view range
            var { activeStart, activeEnd } = this.calendar.view;
            if (startDate < activeStart || startDate > activeEnd || endDate < activeStart || endDate > activeEnd) {
                return;
            }

            this.clearHighlight();

            if (startDate > endDate) {
                var tempDate = startDate;
                startDate = endDate;
                endDate = tempDate;
            }

            var currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                var td = this.cellMap.get(currentDate.toString('yyyy-MM-dd'));
                var bgContainer = td.querySelector('.fc-daygrid-day-bg') || null;
                if (td && bgContainer) {
                    var highlightContainer = document.createElement('div');
                    highlightContainer.classList.add('.fc-daygrid-bg-harness');

                    var highlight = document.createElement('div');
                    highlight.classList.add('fc-highlight');
                    highlightContainer.appendChild(highlight);

                    bgContainer.appendChild(highlightContainer);

                    td.fcMultiMonthHightlight = highlightContainer;

                    this.highlightedCells.push(td);
                }

                currentDate = currentDate.addDays(1);
            }
        }

        private findTdFromEvent(event) {
            if (event.target.tagName === "TD" && event.target.dataset?.date) {
                return event.target;
            }

            let composedPath = event.composedPath();
            for (let i = 0; i < 6 && i < composedPath.length; i++) {
                if (composedPath[i].tagName === "TD" && composedPath[i].dataset?.date) {
                    return composedPath[i];
                }

            }
            return undefined;
        }

    }

    export class BookFullCalendar {
        private calendarElement: HTMLElement;
        private options: IBookFullCalendarOptions;

        private calendar: any;
        public daystatusService: ICalendarDaydataService;
        private eventEmitter: EventEmitter;
        private dateSelection: DateSelection;

        constructor(selector: string, options: IBookFullCalendarOptions) {
            this.calendarElement = document.querySelector(selector);

            const defaultOptions: IBookFullCalendarOptions = {
                schedulerLicenseKey: '',
                culture: 'da',
                houseId: -1,
                userId: -1,
                initialDate: new Date(),
                initialSelection: null,
                mediumBookingBorder: 10,
                minBookingBorder: 5,
                reservationRestrictions: true,
                isDayOpen: [true, true, true, true, true, true, true],
                separateTime: 12,
                showDaysBeforeTodayAsBooked: false,
                showDaysAfterTodayAsBooked: 0,
                isAdmin: false,
                isExternalUser: false,
                showAmountOfMonth: 2,
                calendarStatusDisplayMode: CalendarStatusDisplayMode.Default
            };
            this.options = { ...defaultOptions, ...options };
            this.daystatusService = new CalendarDaydataService(this.options.minBookingBorder, this.options.mediumBookingBorder, this.options.userId, true, this.options.separateTime, this.options.calendarStatusDisplayMode);
            this.eventEmitter = new EventEmitter();

            this.calendar = new FullCalendar.Calendar(this.calendarElement, {
                schedulerLicenseKey: this.options.schedulerLicenseKey,
                themeSystem: 'bootstrap',
                locale: this.options.culture,
                contentHeight: 'auto',

                titleFormat: { year: 'numeric' },
                headerToolbar: {
                    left: 'prev',
                    center: 'title',
                    right: 'next'
                },
                initialDate: this.options.initialDate,
                initialView: 'bookCalendar',
                views: {
                    bookCalendar: {
                        type: 'multiMonth',
                        showNonCurrentDates: false,
                        multiMonthMaxColumns: 2,
                        duration: {
                            months: this.options.showAmountOfMonth
                        },
                        dateIncrement: { months: 1 },
                        multiMonthTitleFormat: {
                            month: 'long'
                        }
                    }
                },
                weekNumbers: true,
                weekNumberFormat: {
                    week: 'numeric'
                },
                selectable: false,
                events: this.fetchEvents.bind(this),
                displayEventTime: false,
                displayEventEnd: false,
                eventDisplay: 'block',
                eventClick: (eventClickInfo) => {
                    eventClickInfo.jsEvent.preventDefault();
                },
                eventMouseEnter: (mouseEnterInfo) => {
                    this.eventEmitter.trigger('eventEnter', mouseEnterInfo);
                },
                eventMouseLeave: (mouseLeaveInfo) => {
                    this.eventEmitter.trigger('eventLeave', mouseLeaveInfo);
                },
                datesSet: (dateInfo) => {
                    //this.updateToolbarButtons(dateInfo);
                    this.eventEmitter.trigger('datesSet', dateInfo);
                },
                select: (selectionInfo) => {
                    this.eventEmitter.trigger('select', selectionInfo);
                }
            });
            this.dateSelection = new DateSelection(this.calendar, this.options);
        }

        //private updateToolbarButtons(dateInfo): void {
        //    var { el: calendarEl }: { el: HTMLElement } = this.calendar;

        //    var monthTitles = calendarEl.querySelectorAll('.fc-multimonth-header .fc-multimonth-title');
        //    if (monthTitles && monthTitles.length == 2) { // We only want to titles of buttons, if the view is showing 2 months

        //        var prevButton = calendarEl.querySelector('.fc-header-toolbar .fc-prev-button');
        //        var prevTextNode = Array.from(prevButton.childNodes).find(item => item.nodeType == 3);
        //        if (prevTextNode) {
        //            prevTextNode.nodeValue = monthTitles[0].textContent;
        //        }
        //        else {
        //            var textElement = document.createTextNode(monthTitles[0].textContent);
        //            prevButton.appendChild(textElement)
        //        }

        //        var nextButton = calendarEl.querySelector('.fc-header-toolbar .fc-next-button');
        //        var nextTextNode = Array.from(nextButton.childNodes).find(item => item.nodeType == 3);
        //        if (nextTextNode) {
        //            nextTextNode.nodeValue = monthTitles[1].textContent;
        //        }
        //        else {
        //            var textElement = document.createTextNode(monthTitles[1].textContent);
        //            nextButton.insertBefore(textElement, nextButton.firstChild);
        //        }
        //    }
        //}

        private fetchEvents(info, success, failure) {
            this.daystatusService.loadDataAjax(this.options.houseId, info.start, info.end).then(() => {

                var fullCalendarEvents = [];
                var today = new Date().getDateWithoutTime();
                var currentDate = new Date(info.start.getTime());
                var activeDate = new Date(today.getTime()).addDays(this.options.showDaysAfterTodayAsBooked);
                do {
                    var restrictions = this.options.reservationRestrictions;

                    var dayStatus = this.daystatusService.getStatusForDay(currentDate, this.options.isExternalUser);

                    var dayHasPrice = this.daystatusService.dayHasPrice(currentDate);
                    if (!dayHasPrice) {
                        dayStatus = "noprice";
                    }
                    //debugger;
                    var dayActive = "";
                    var disableSysMaxForDay = this.daystatusService.sysMaxDateGælderIkkeForLodtrækninger && dayStatus.indexOf('drawperiod') > -1;
                    if ((this.options.reservationRestrictions && (!dayHasPrice || currentDate < this.daystatusService.mindate || (currentDate > this.daystatusService.houseOpenUntill && !disableSysMaxForDay)) || (currentDate < today && this.options.reservationRestrictions) || dayStatus.indexOf('restrictedPeriod') > -1) ||
                        this.options.isDayOpen[currentDate.getDay()] === false) {
                        dayActive = "day-not-active";
                    }
                    dayStatus = dayStatus.replace('restrictedPeriod', '');

                    if (!this.options.isAdmin && this.options.showDaysBeforeTodayAsBooked && currentDate.getTime() < activeDate.getTime()) {
                        dayStatus = "reservation";
                    }

                    fullCalendarEvents.push({
                        start: currentDate,
                        end: currentDate,
                        display: 'background',
                        classNames: [dayStatus, dayActive],
                        allDay: true
                    });

                    currentDate = currentDate.addDays(1);
                }
                while (currentDate.getTime() <= info.end.getTime());

                this.daystatusService.getLoadedEvents()
                    .forEach(event => {
                        console.log("TT", event)
                        fullCalendarEvents.push({
                            id: event.id,
                            start: event.startdate,
                            //end: new Date(event.enddate).addDays(1),
                            end: new Date(event.enddate),
                            title: event.eventName,
                            description: event.periodDescription
                        });
                    });

                success(fullCalendarEvents);
            });
        }

        on(event: string, handler: Function): void {
            this.eventEmitter.on(event, handler);
        };

        off(event: string, handler?: Function): void {
            this.eventEmitter.off(event, handler);
        };

        render(): void {
            this.calendar.render();

            if (this.options.initialSelection) {
                var startDate = new Date(this.options.initialSelection.startDate) || new Date();
                var endDate = new Date(this.options.initialSelection.endDate || this.options.initialSelection.startDate) || new Date();

                this.calendar.select(startDate, endDate);
            }
        }
    }
}